import { createEffect, createMemo, createSignal, For, Show, type JSX } from 'solid-js';
import { deployOrder, canDeploy, shortId } from '../lib/format';
import { flatten, useBoxes, useBoxStacks, useDeployStack, useStacks } from '../lib/queries';
import { pushToast } from '../lib/toast';
import type { BoxListItem, StackWithManifest } from '../lib/types';
import { Button } from '../ui/Button';
import { Dialog } from '../ui/Dialog';
import { CheckboxCard, Select } from '../ui/Field';
import { Icon } from '../ui/Icon';
import { ErrorAlert } from './states';

/** Deploys a catalog stack onto a box. Either side can be fixed: from a box
 *  the user picks a stack, from the catalog they pick a target box. */
export function DeployStackDialog(props: {
  open: boolean;
  box?: Pick<BoxListItem, 'box_id' | 'display'>;
  stack?: StackWithManifest;
  onClose: () => void;
}): JSX.Element {
  const deploy = useDeployStack();
  const stacks = useStacks();
  const boxes = useBoxes();
  const [chosenStacks, setChosenStacks] = createSignal<string[]>([]);
  const [chosenBox, setChosenBox] = createSignal('');

  const stackItems = createMemo(() =>
    flatten(stacks.data?.pages)
      .filter((stack): stack is StackWithManifest => stack.manifest === '1'),
  );
  const readyBoxes = createMemo(() =>
    flatten(boxes.data?.pages).filter((box) => canDeploy(box.box_status, box.ec2_system_status)),
  );
  const boxOptions = createMemo(() =>
    readyBoxes().map((box) => ({
      value: box.box_id,
      label: `${box.display} · ${shortId(box.box_id)} · ${box.hostname ?? 'no address'}`,
    })),
  );

  const stackIds = () => props.stack ? [props.stack.slug] : deployOrder(chosenStacks());
  const boxId = () => props.box?.box_id ?? (chosenBox() || boxOptions()[0]?.value || '');
  const boxStacks = useBoxStacks(boxId, () => boxId() !== '');
  const deployed = createMemo(() => flatten(boxStacks.data?.pages));
  const alreadyDeployed = () => deployed().filter((stack) => stackIds().includes(stack.stack_id));
  const checkingDeployed = () => boxStacks.isPending || boxStacks.isFetchingNextPage || boxStacks.hasNextPage;
  const allStacksSelected = () => stackItems().length > 0 && chosenStacks().length === stackItems().length;
  createEffect(() => {
    if (boxStacks.hasNextPage && !boxStacks.isFetchingNextPage) void boxStacks.fetchNextPage();
  });
  createEffect(() => {
    if (stacks.hasNextPage && !stacks.isFetchingNextPage) void stacks.fetchNextPage();
  });
  // Every open starts with everything selected, once the whole catalog has loaded.
  let stacksPreselected = false;
  createEffect(() => {
    if (!props.open) {
      stacksPreselected = false;
      return;
    }
    if (stacksPreselected || !stacks.isSuccess || stacks.hasNextPage) return;
    stacksPreselected = true;
    setChosenStacks(deployOrder(stackItems().map((stack) => stack.slug)));
  });

  function toggleStack(slug: string, checked: boolean): void {
    setChosenStacks((current) => (checked ? deployOrder([...current, slug]) : current.filter((item) => item !== slug)));
  }

  async function deployStack(): Promise<void> {
    if (stackIds().length === 0 || !boxId()) return;
    try {
      await deploy.mutateAsync({ boxId: boxId(), stackIds: stackIds() });
      pushToast('success', `Deployment started on ${shortId(boxId())}.`);
      props.onClose();
    } catch {
      // Shown in the dialog.
    }
  }

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      title="Deploy stacks"
      description="Queues the selected stacks for installation on the box and starts them there."
      footer={
        <>
          <Button variant="secondary" onClick={props.onClose} disabled={deploy.isPending}>
            Cancel
          </Button>
          <Button
            loading={deploy.isPending}
            disabled={stackIds().length === 0 || !boxId() || checkingDeployed() || boxStacks.isError}
            onClick={() => void deployStack()}
          >
            Deploy
          </Button>
        </>
      }
    >
      <div class="stack gap-4">
        <Show
          when={!props.stack}
          fallback={
            <div class="kv">
              <span class="kv__k">Stack</span>
              <span class="kv__v">
                {props.stack?.display} · {props.stack?.revision}
              </span>
            </div>
          }
        >
          <Show
            when={stackItems().length > 0}
            fallback={<span class="muted text-sm">No stacks are registered in the catalog yet.</span>}
          >
            <div class="stack gap-2">
              <div class="row" style={{ 'justify-content': 'space-between' }}>
                <span class="tk-label">Stacks</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setChosenStacks(allStacksSelected() ? [] : deployOrder(stackItems().map((stack) => stack.slug)))}
                >
                  {allStacksSelected() ? 'Deselect all' : 'Select all'}
                </Button>
              </div>
              <div class="key-picker">
                <For each={stackItems()}>
                  {(stack) => (
                    <CheckboxCard
                      checked={chosenStacks().includes(stack.slug)}
                      onChange={(checked) => toggleStack(stack.slug, checked)}
                      title={stack.display}
                      subtitle={`${stack.slug} · rev ${stack.revision}`}
                    />
                  )}
                </For>
              </div>
            </div>
          </Show>
        </Show>

        <Show
          when={!props.box}
          fallback={
            <div class="kv">
              <span class="kv__k">Target box</span>
              <span class="kv__v">{props.box?.display}</span>
              <span class="kv__k">Box ID</span>
              <span class="kv__v kv__v--mono">{props.box?.box_id}</span>
            </div>
          }
        >
          <Show
            when={boxOptions().length > 0}
            fallback={<span class="muted text-sm">No box is Ready with a passing system check.</span>}
          >
            <Select
              label="Target box"
              options={boxOptions()}
              value={boxId()}
              onChange={(event) => setChosenBox(event.currentTarget.value)}
            />
          </Show>
        </Show>

        <Show when={alreadyDeployed().length > 0}>
          <div class="alert alert--warning">
            <Icon name="alert" size={18} />
            <div class="alert__body">
              <span class="alert__title">Some selected stacks are already deployed</span>
              <span>
                Deploying {alreadyDeployed().map((stack) => stack.display).join(', ')} again will replace their files on this box.
              </span>
            </div>
          </div>
        </Show>
        <Show when={checkingDeployed()}>
          <span class="muted text-sm">Checking deployed stacks…</span>
        </Show>
        <Show when={boxStacks.isError}>
          <ErrorAlert title="Could not check deployed stacks" error={boxStacks.error} />
        </Show>
      </div>
      <Show when={deploy.isError}>
        <ErrorAlert title="Could not start the deployment" error={deploy.error} />
      </Show>
    </Dialog>
  );
}
